"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateAttachmentInCachedDrops, updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import type { ApiAttachment } from "@/generated/models/ApiAttachment";
import type { ApiCurationDropsPage } from "@/generated/models/ApiCurationDropsPage";
import type { ApiDropWithoutWave } from "@/generated/models/ApiDropWithoutWave";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { toApiWaveMin } from "@/helpers/waves/wave.helpers";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDebouncedQueryRefetch } from "./useDebouncedQueryRefetch";
const DEFAULT_WAVE_CURATION_DROPS_PAGE_SIZE = 20;
export function useWaveCurationDrops(t0) {
  const $ = _c(45);
  const {
    wave,
    curationId,
    pageSize: t1,
    enabled: t2
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_WAVE_CURATION_DROPS_PAGE_SIZE : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== curationId) {
    t3 = curationId?.trim() ?? "";
    $[0] = curationId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedCurationId = t3;
  const waveId = wave?.id ?? null;
  const queryClient = useQueryClient();
  let t4;
  if ($[2] !== wave) {
    t4 = wave ? toApiWaveMin(wave) : null;
    $[2] = wave;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const waveMin = t4;
  const t5 = normalizedCurationId || null;
  let t6;
  if ($[4] !== pageSize || $[5] !== t5 || $[6] !== waveId) {
    t6 = [QueryKey.DROPS, {
      waveId,
      curationId: t5,
      pageSize,
      context: "wave-curation-drops"
    }];
    $[4] = pageSize;
    $[5] = t5;
    $[6] = waveId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const queryKey = t6 as const;
  let t7;
  if ($[8] !== normalizedCurationId || $[9] !== pageSize || $[10] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam
      } = t8;
      if (!waveId || !normalizedCurationId) {
        throw new Error("Wave and curation are required to load curation drops");
      }
      return await commonApiFetch({
        endpoint: `waves/${waveId}/curations/${normalizedCurationId}/drops`,
        params: {
          page: String(pageParam),
          page_size: String(pageSize)
        }
      });
    };
    $[8] = normalizedCurationId;
    $[9] = pageSize;
    $[10] = waveId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = enabled && !!waveId && normalizedCurationId.length > 0;
  let t9;
  if ($[12] !== queryKey || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      queryKey,
      queryFn: t7,
      enabled: t8,
      initialPageParam: 1,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    };
    $[12] = queryKey;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data,
    dataUpdatedAt,
    error,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    refetch
  } = useInfiniteQuery(t9);
  let t10;
  if ($[16] !== hasNextPage || $[17] !== isFetchingNextPage || $[18] !== onFetchNextPage) {
    t10 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = onFetchNextPage;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const fetchNextPage = t10;
  data?.pages;
  let t11;
  bb0: {
    if (!waveMin) {
      let t12;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[20] = t12;
      } else {
        t12 = $[20];
      }
      t11 = t12;
      break bb0;
    }
    let t12;
    if ($[21] !== data?.pages || $[22] !== waveMin) {
      const pages = data?.pages.map(page => ({
        wave: waveMin,
        drops: page.data as ApiDropWithoutWave[]
      })) ?? [];
      t12 = generateUniqueKeys(mapToExtendedDrops(pages, [], false), []);
      $[21] = data?.pages;
      $[22] = waveMin;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    t11 = t12;
  }
  const drops = t11;
  let t12;
  if ($[24] !== isFetching || $[25] !== isFetchingNextPage || $[26] !== refetch) {
    t12 = {
      refetch,
      isFetching,
      isFetchingNextPage
    };
    $[24] = isFetching;
    $[25] = isFetchingNextPage;
    $[26] = refetch;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const requestRefetch = useDebouncedQueryRefetch(t12);
  let t13;
  if ($[28] !== queryClient || $[29] !== requestRefetch || $[30] !== waveId) {
    t13 = message => {
      if (waveId !== message.wave.id) {
        return;
      }
      updateDropInCachedDrops(queryClient, message);
      requestRefetch();
    };
    $[28] = queryClient;
    $[29] = requestRefetch;
    $[30] = waveId;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t13);
  let t14;
  if ($[32] !== queryClient) {
    t14 = attachment => {
      updateAttachmentInCachedDrops(queryClient, attachment);
    };
    $[32] = queryClient;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  useWebSocketMessage(WsMessageType.ATTACHMENT_STATUS_UPDATE, t14);
  let t15;
  if ($[34] !== dataUpdatedAt || $[35] !== drops || $[36] !== error || $[37] !== fetchNextPage || $[38] !== hasNextPage || $[39] !== isError || $[40] !== isFetching || $[41] !== isFetchingNextPage || $[42] !== isPlaceholderData || $[43] !== refetch) {
    t15 = {
      dataUpdatedAt,
      drops,
      error,
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching,
      isFetchingNextPage,
      isPlaceholderData,
      refetch
    };
    $[34] = dataUpdatedAt;
    $[35] = drops;
    $[36] = error;
    $[37] = fetchNextPage;
    $[38] = hasNextPage;
    $[39] = isError;
    $[40] = isFetching;
    $[41] = isFetchingNextPage;
    $[42] = isPlaceholderData;
    $[43] = refetch;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}